import * as React from 'react';
import styled from 'styled-components';
import { Callout, ICalloutProps } from '../../callout';
import { IBaseComponentProps } from '../../types';
import { PopupHeader } from './popup-header';
import { PopupContent } from './popup-content';
import { PopupFooter } from './popup-footer';
import { IHostableSurfaceContextProps } from '../../hostable-surface/hostable-surface-context';
import { IPopupProps } from '../popup-props';
import { withHostablePanel } from '../../hostable-surface';
import {
    IMoveableSurfaceContextProps, IMoveableSurfaceProps,
    makeMoveable, MoveableHandlerArguments, MoveableSurface
} from '../../moveable';
import { StyledMoveableWrapper } from './moveable-popup';

type ICalloutContentProps = Pick<IPopupCalloutProps, 'className' | 'elementRef'>;

interface IPopupCalloutProps extends
    IBaseComponentProps<HTMLDivElement>,
    Pick<IPopupProps, 'onRenderContent' | 'onRenderFooter' | 'onInjectCustomContent' | 'showModal' | 'onRenderTitle' | 'footerAlignment' | 'moveable' | 'useFullHeight' | 'useFullWidth' | 'titleAlignment'>,
    Pick<IHostableSurfaceContextProps, 'getSurfaceClientRect'> {
    hideCloseButton?: boolean;
    title?: string;
    onCloseClick(): void;
}

const StyledCallout = styled(Callout) <ICalloutProps>`
    border-radius: ${({ theme: { popup: { composites } } }) => composites.popupCalloutBorderRadius};
    border: none;
    z-index: 5;
    .ap-callout-content {
        border-radius: ${({ theme: { popup: { composites } } }) => composites.popupCalloutBorderRadius};
        overflow: hidden;
    }
    .ap-moveable-wrapper {
       position: absolute;
    }
`;

const MoveablePopupCalloutSurface: React.ComponentType<IMoveableSurfaceProps<IMoveableSurfaceContextProps<IPopupCalloutProps>>> = MoveableSurface;

const CalloutContentWrapper = styled.div.attrs(({ className, elementRef }: ICalloutContentProps) => ({
    className: className,
    ref: elementRef
})) <ICalloutContentProps>``;

const MoveableStyledWrapper = makeMoveable<{}, ICalloutProps>(StyledMoveableWrapper, ref => ({ elementRef: ref }));

class PopupCalloutBase extends React.PureComponent<IPopupCalloutProps> {

    private frame = { translate: [0, 0] };

    private hostRef = React.createRef<HTMLDivElement>();
    private targetRef = React.createRef<HTMLDivElement>();

    private _onDragStart = ({ set }: MoveableHandlerArguments<'onDragStart'>) => set(this.frame.translate);

    private _onDrag = (({ beforeTranslate }: MoveableHandlerArguments<'onDrag'>) => {
        this.frame.translate = beforeTranslate;
        if (this.targetRef.current) {
            (this.targetRef.current.style.transform = `translate(${beforeTranslate[0]}px, ${beforeTranslate[1]}px)`);
        }
    });

    private renderCalloutContentWrapper() {
        const { className, elementRef } = this.props;
        return (
            <CalloutContentWrapper className={className} elementRef={elementRef}>
                {this.renderCalloutContent()}
            </CalloutContentWrapper>
        );
    }

    private renderStyledCallout() {
        const { showModal, useFullHeight, useFullWidth } = this.props;
        return (
            <StyledCallout
                preventDismissOnFocusOut
                elementRef={this.targetRef}
                showModal={showModal}
                useFullHeight={useFullHeight}
                useFullWidth={useFullWidth}
                autofocusOnOpen>
                {this.renderCalloutContentWrapper()}
            </StyledCallout >
        );
    }

    render() {
        return this.renderStyledCallout();
    }

    private renderMoveable() {
        const { moveable } = this.props;

        if (moveable) {
            const moveableConfigs = {
                draggable: true,
                highlightMoveable: false,
                throttleDrag: 0,
                origin: false,
                onDragStart: this._onDragStart,
                onDrag: this._onDrag
            };
            return (

                <MoveablePopupCalloutSurface context={{ ...this.props, containerRef: this.hostRef }}>
                    {(ref) => (<MoveableStyledWrapper
                        moveable={moveableConfigs}
                        elementRef={ref as React.RefObject<HTMLDivElement>} />)}
                </MoveablePopupCalloutSurface>
            );
        }

        return null;
    }

    private renderHeader() {
        const { hideCloseButton, title, titleAlignment, onCloseClick, onRenderTitle } = this.props;
        return (!hideCloseButton || title || onRenderTitle) ? (
            <PopupHeader hideCloseButton={hideCloseButton} title={title} titleAlignment={titleAlignment} onCloseClick={onCloseClick} onRenderTitle={onRenderTitle} />
        ) : null;
    }

    private renderContent() {
        const { onRenderContent, onRenderFooter, getSurfaceClientRect } = this.props;
        const { height } = getSurfaceClientRect();
        return onRenderContent ? (
            <PopupContent onRenderContent={onRenderContent} doesFooterExists={onRenderFooter !== undefined}
                clientHeight={height} />
        ) : null;
    }

    private renderFooter() {
        const { onRenderFooter, footerAlignment } = this.props;
        return <PopupFooter onRenderFooter={onRenderFooter} footerAlignment={footerAlignment} />;
    }

    private renderCalloutContent() {
        const { onInjectCustomContent } = this.props;
        const content = (
            <>
                {this.renderMoveable()}
                {this.renderHeader()}
                {this.renderContent()}
                {this.renderFooter()}
            </>
        );
        return onInjectCustomContent ? onInjectCustomContent(content) : content;
    }
}

export const PopupCallout = styled(withHostablePanel(PopupCalloutBase)).attrs(() => ({
    className: String('ap-popup')
}))`
    min-width: 300px;
    min-height: 100px;
    background-color: ${({ theme: { popup: { colors } } }) => colors.popupCalloutBackgroundColor};
    box-sizing: border-box;
    position: relative;
    padding: ${({ theme: { semantics: { gap } } }) => gap.xl};
`;
